import { cn } from "@client/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
	"py-2 px-3 text-white flex items-center justify-center text-center rounded-[calc(12*var(--ui-px))] transition-all [&_svg]:size-6 active:scale-97 disabled:pointer-events-none disabled:opacity-60",
	{
		variants: {
			responsive: {
				true: "mobile-body-1-bold lg:body-1-bold gap-2 lg:gap-3",
				false: "body-1-bold gap-3",
			},
			variant: {
				default: "bg-slate-750 hover:bg-slate-800 active:bg-slate-900 ",
				ghost: "bg-transparent hover:text-white text-slate-400 py-0 px-0",
				positive:
					"bg-green-700 hover:bg-green-800 active:bg-green-900 ",
				negative: "bg-red-700 hover:bg-red-800 active:bg-red-900 ",
				cold: "bg-cyan-700 hover:bg-cyan-800 active:bg-cyan-900 ",
				royal: "bg-violet-700 hover:bg-violet-800 active:bg-violet-900 ",
				lime: "bg-lime-700 hover:bg-lime-800 active:bg-lime-900 ",
				legendary:
					"bg-orange-700 hover:bg-orange-800 active:bg-orange-900 ",
				emerald:
					"bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 ",
				healthy: "bg-blue-700 hover:bg-blue-800 active:bg-blue-900 ",
				sweet: "bg-pink-700 hover:bg-pink-800 active:bg-pink-900 ",
				white: "bg-white text-slate-900 hover:bg-stone-200 active:bg-stone-300",
				disabled:
					"text-slate-400 border border-transparent hover:border-slate-500 cursor-not-allowed",
			},
		},
		defaultVariants: {
			responsive: true,
			variant: "default",
		},
	},
);

interface ButtonProps extends React.ComponentProps<"button"> {
	loading?: boolean;
}

function Button({
	className,
	variant,
	responsive,
	children,
	loading,
	...props
}: ButtonProps & VariantProps<typeof buttonVariants>) {
	return (
		<button
			data-slot="button"
			className={cn(
				buttonVariants({ variant, responsive, className }),
				loading &&
					"pointer-events-none relative cursor-wait overflow-hidden",
			)}
			{...props}
		>
			{children}
		</button>
	);
}

export { Button, buttonVariants };
